// Input validation utility functions
import DOMPurify from 'dompurify';

/**
 * Sanitizes user input to prevent XSS attacks using DOMPurify
 * @param {string} input - The input string to sanitize
 * @returns {string} - The sanitized string
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  // Use DOMPurify for comprehensive XSS protection
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [], // Remove all HTML tags by default
    ALLOWED_ATTR: [], // Remove all attributes
    KEEP_CONTENT: true // Keep text content of removed tags
  });
};

/**
 * Sanitizes HTML content while allowing safe HTML tags
 * @param {string} html - The HTML content to sanitize
 * @returns {string} - The sanitized HTML string
 */
export const sanitizeHTML = (html) => {
  if (typeof html !== 'string') return html;
  
  // Allow only safe HTML tags for content display
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li', 'code', 'pre'],
    ALLOWED_ATTR: ['href', 'title', 'target'],
    KEEP_CONTENT: true
  });
};

/**
 * Validates email format
 * @param {string} email - The email to validate
 * @returns {boolean} - True if email is valid
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validates username format
 * @param {string} username - The username to validate
 * @returns {boolean} - True if username is valid
 */
export const isValidUsername = (username) => {
  // Username should be 3-20 characters, alphanumeric and underscores only
  const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;
  return usernameRegex.test(username);
};

/**
 * Validates that input doesn't contain dangerous characters
 * @param {string} input - The input to validate
 * @returns {boolean} - True if input is safe
 */
export const isInputSafe = (input) => {
  // Check for potentially dangerous characters/sequences
  const dangerousPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+=/i,
    /eval\(/i,
    /expression\(/i,
    /data:/i
  ];
  
  return !dangerousPatterns.some(pattern => pattern.test(input));
};

/**
 * Validates and sanitizes user data with enhanced security
 * @param {object} data - The data object to validate
 * @returns {object} - Object containing isValid flag and sanitized data
 */
export const validateAndSanitizeData = (data) => {
  const sanitizedData = {};
  let isValid = true;
  const errors = [];

  for (const [key, value] of Object.entries(data)) {
    // Skip non-string values
    if (typeof value !== 'string') {
      sanitizedData[key] = value;
      continue;
    }

    // Check for dangerous input patterns
    if (!isInputSafe(value)) {
      isValid = false;
      errors.push(`Invalid input for field ${key}`);
      continue;
    }

    // Additional validation for length
    if (value.length > 1000) { // Limit input length
      isValid = false;
      errors.push(`Input for field ${key} is too long`);
      continue;
    }

    // Sanitize the input using DOMPurify
    sanitizedData[key] = sanitizeInput(value);
  }

  return {
    isValid,
    data: sanitizedData,
    errors
  };
};